import { MutationCache, QueryClient, VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'
import { notifyError, notifySuccess } from '@/lib/notify'

/**
 * Cache serveur de la SPA (TanStack Query).
 *
 * Le cache est la seule source de vérité pour les données distantes : pas de
 * store Pinia qui recopierait les réponses de l'API. Un store ne sert qu'à
 * l'état purement client (préférences d'affichage, brouillons de formulaire).
 *
 * Retours d'action (SP-27, SP-37) : le cache de mutations parle au toaster. Une mutation
 * qui déclare `meta.success` l'annonce quand elle aboutit ; toute mutation qui échoue est
 * annoncée avec le message de l'API, sauf `meta.silent` — réservé aux formulaires qui
 * traduisent eux-mêmes un statut (409, 403…) et appellent alors `notifyError`. Jamais de
 * donnée de santé dans un toast : seuls des libellés fixes et le message d'erreur de l'API
 * y passent.
 */
declare module '@tanstack/vue-query' {
  interface Register {
    mutationMeta: { success?: string; silent?: boolean }
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      const message = mutation.meta?.success
      if (message) notifySuccess(message)
    },
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.silent) return
      notifyError(error)
    },
  }),
  defaultOptions: {
    queries: {
      // Données médicales : une fenêtre courte, mais suffisante pour éviter
      // qu'un changement d'onglet ne relance toutes les requêtes de la page.
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Ne pas réessayer sur une erreur de contrat ou d'autorisation.
        const status = (error as { status?: number })?.status
        if (status !== undefined && status >= 400 && status < 500) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: false,
    },
  },
})

export const vueQueryOptions: VueQueryPluginOptions = { queryClient }
export { VueQueryPlugin }
